import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Clock, 
  Shield, 
  Trophy, 
  BookOpen, 
  ChevronRight, 
  CheckCircle,
  Play,
  Award,
  Filter,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../hooks/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Module, Section } from '../../types';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

interface UserProgressLocal {
  completedModules?: string[];
  moduleProgress?: Record<string, number>;
}

type ModuleEx = Module & {
  description?: string;
  completionCount?: number;
  isPublished?: boolean;
  sections?: Section[];
};

export const TrainingHub: React.FC = () => {
  const { user } = useAuth();
  const { addEvent } = useAnalytics();
  const [modules, setModules] = useState<Module[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgressLocal | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'order' | 'newest' | 'popular' | 'difficulty'>('order');

  // Badge mapper for static Tailwind classes
  const badgeClasses = (minutes: number) => {
    if (minutes <= 5) {
      return { label: 'Beginner', bg: 'bg-emerald-100', text: 'text-emerald-700', Icon: BookOpen };
    }
    if (minutes <= 15) {
      return { label: 'Intermediate', bg: 'bg-amber-100', text: 'text-amber-700', Icon: Trophy };
    }
    return { label: 'Advanced', bg: 'bg-rose-100', text: 'text-rose-700', Icon: Award };
  };

  const getThumbnailUrl = (module: Module): string | undefined => {
    if (module.coverImage) return module.coverImage;
    const firstMediaSection = module.sections?.find(s => s.media?.thumbnail);
    return firstMediaSection?.media?.thumbnail || firstMediaSection?.media?.downloadURL;
  };

  // Filter and sort modules
  const filteredModules = useMemo(() => {
    const filteredList = modules.filter((module: ModuleEx) => {
      const desc = module.description || module.summary || '';
      const matchesSearch = module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           desc.toLowerCase().includes(searchTerm.toLowerCase());
      const isTrainingModule = module.moduleType !== 'meditation';
      return matchesSearch && isTrainingModule;
    });

    switch (sortBy) {
      case 'order':
        // Sort by display order (ascending - lower order numbers appear first)
        filteredList.sort((a, b) => (a.order || 999) - (b.order || 999));
        break;
      case 'popular':
        filteredList.sort((a: ModuleEx, b: ModuleEx) => (b.completionCount || 0) - (a.completionCount || 0));
        break;
      case 'difficulty':
        filteredList.sort((a, b) => (a.estMinutes || 0) - (b.estMinutes || 0));
        break;
      case 'newest':
        filteredList.sort((a: ModuleEx, b: ModuleEx) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      default:
        // Default to display order
        filteredList.sort((a, b) => (a.order || 999) - (b.order || 999));
        break;
    }

    return filteredList;
  }, [modules, searchTerm, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    const completedModules = userProgress?.completedModules || [];
    const totalModules = modules.filter((m: ModuleEx) => m.moduleType !== 'meditation').length;
    const completionRate = totalModules > 0 ? (completedModules.length / totalModules) * 100 : 0;
    
    return {
      totalModules,
      completedModules: completedModules.length,
      completionRate: Math.round(completionRate),
      totalMinutes: modules.reduce((sum, module) => sum + (module.estMinutes || 0), 0)
    };
  }, [modules, userProgress]);

  // Load modules and user progress
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Try to load all modules with order field first
        let modulesSnapshot;
        try {
          const modulesQuery = query(
            collection(db, 'modules'),
            orderBy('order', 'asc')
          );
          modulesSnapshot = await getDocs(modulesQuery);
          console.log('📊 Total modules in DB (ordered):', modulesSnapshot.docs.length);
        } catch (orderError) {
          console.warn('⚠️ Order query failed, trying without order:', orderError);
          try {
            const modulesQuery = query(
              collection(db, 'modules')
            );
            modulesSnapshot = await getDocs(modulesQuery);
            console.log('📊 Total modules in DB:', modulesSnapshot.docs.length);
          } catch (error) {
            console.error('Error loading modules:', error);
            setModules([]);
            setLoading(false);
            return;
          }
        }
        
        const allModules = modulesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            order: data.order ?? 999 // Default high order for items without order field
          };
        }) as Module[];
        
        console.log('📝 All modules:', allModules.map(m => ({
          id: m.id,
          title: m.title,
          published: (m as ModuleEx).published,
          isPublished: (m as ModuleEx).isPublished,
          moduleType: m.moduleType,
          order: m.order
        })));
        
        // Filter for training modules that are published (flexible check)
        const publishedModules = allModules.filter((module: ModuleEx) => {
          const isPublished = module.published === true || module.isPublished === true || module.published === undefined;
          const isTraining = module.moduleType !== 'meditation';
          console.log(`🔍 Module "${module.title}": published=${isPublished}, isTraining=${isTraining}, order=${module.order}`);
          return isPublished && isTraining;
        });
        
        // Sort by order field (ascending)
        publishedModules.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 999;
          const orderB = typeof b.order === 'number' ? b.order : 999;
          return orderA - orderB;
        });
        
        console.log('✅ Filtered and sorted modules:', publishedModules.length);
        setModules(publishedModules);

        if (user) {
          const progressQuery = query(
            collection(db, 'userProgress'),
            where('uid', '==', user.uid)
          );
          const progressSnapshot = await getDocs(progressQuery);
          if (!progressSnapshot.empty) {
            const progressData = progressSnapshot.docs[0].data() as UserProgressLocal;
            setUserProgress(progressData);
          }
        }
      } catch (error) {
        console.error('Error loading training data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading training modules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Mobile: Single narrow container | Desktop: Wide container */}
      <div className="w-full max-w-screen-sm lg:max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 lg:py-6 min-w-0">
        
        {/* Hero Section - Mobile: Compact | Desktop: Wide Dashboard */}
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-2xl lg:rounded-3xl p-5 lg:p-8 shadow-lg mb-4 lg:mb-6 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 lg:w-64 lg:h-64 bg-white/10 rounded-full -mr-16 -mt-16 lg:-mr-32 lg:-mt-32"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 lg:w-48 lg:h-48 bg-white/10 rounded-full -ml-12 -mb-12 lg:-ml-24 lg:-mb-24"></div>
          
          <div className="relative z-10">
            {/* Mobile: Stacked | Desktop: Side by Side */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8 mb-4 lg:mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm p-2.5 lg:p-4 rounded-xl lg:rounded-2xl">
                  <Shield className="w-7 h-7 lg:w-10 lg:h-10 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl lg:text-3xl font-bold text-white truncate">Training Center</h1>
                  <p className="text-blue-100 text-sm lg:text-base">Build resilience & mental strength</p>
                </div>
              </div>
            </div>
            
            {/* Stats Grid - Mobile: 2x2 | Desktop: 1x4 */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 lg:gap-4">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 lg:p-5 text-center">
                <div className="text-2xl lg:text-4xl font-bold text-white">{stats.totalModules}</div>
                <div className="text-xs lg:text-sm text-blue-100 mt-0.5 lg:mt-1">Modules</div>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 lg:p-5 text-center">
                <div className="text-2xl lg:text-4xl font-bold text-white">{stats.completedModules}</div>
                <div className="text-xs lg:text-sm text-blue-100 mt-0.5 lg:mt-1">Completed</div>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 lg:p-5 text-center">
                <div className="text-2xl lg:text-4xl font-bold text-white">{stats.completionRate}%</div>
                <div className="text-xs lg:text-sm text-blue-100 mt-0.5 lg:mt-1">Progress</div>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 lg:p-5 text-center">
                <div className="text-2xl lg:text-4xl font-bold text-white">{stats.totalMinutes}</div>
                <div className="text-xs lg:text-sm text-blue-100 mt-0.5 lg:mt-1">Minutes</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter Bar - Sticky | Desktop: Horizontal Layout */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md rounded-xl lg:rounded-2xl shadow-sm border border-gray-200 p-3 lg:p-4 mb-4 lg:mb-6 w-full">
          {/* Mobile: Stacked | Desktop: Horizontal */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 lg:w-5 lg:h-5" />
              <input
                type="text"
                placeholder="Search training modules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 lg:pl-11 pr-3 py-2 lg:py-2.5 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Sort Row */}
            <div className="flex items-center justify-between lg:justify-start gap-3 lg:gap-4">
              <div className="flex items-center gap-2 text-sm lg:text-base text-gray-600">
                <Filter className="w-4 h-4 lg:w-5 lg:h-5" />
                <span className="font-medium">Sort by</span>
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'order' | 'newest' | 'popular' | 'difficulty')}
                className="text-sm lg:text-base border border-gray-300 rounded-lg px-3 lg:px-4 py-1.5 lg:py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[140px] lg:min-w-[160px]"
              >
                <option value="order">Display Order</option>
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
                <option value="difficulty">Difficulty</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modules List - Mobile: Vertical | Desktop: Grid */}
        <div className="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 lg:space-y-0 w-full overflow-x-hidden">
          {filteredModules.map((module) => {
            const badge = badgeClasses(module.estMinutes || 0);
            const isCompleted = userProgress?.completedModules?.includes(module.id) || false;
            const progress = userProgress?.moduleProgress?.[module.id] || 0;
            const thumb = getThumbnailUrl(module);
            
            return (
              <Link
                key={module.id}
                to={`/training/${module.id}`}
                className="block bg-white rounded-xl lg:rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-blue-300 hover:-translate-y-1 transition-all duration-200 overflow-hidden group w-full"
                onClick={() => addEvent('module.view', { moduleId: module.id })}
              >
                <div className="flex lg:flex-col gap-3 lg:gap-0 p-3 lg:p-0 w-full min-w-0 max-w-full">
                  {/* Thumbnail - Mobile: Small Square | Desktop: Full Width Rectangle */}
                  <div className="relative w-20 h-20 lg:w-full lg:h-48 rounded-lg lg:rounded-t-2xl lg:rounded-b-none overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 flex-shrink-0">
                    {thumb ? (
                      <img 
                        src={thumb} 
                        alt={module.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play className="w-6 h-6 lg:w-12 lg:h-12 text-white/90" />
                      </div>
                    )}
                    
                    {/* Progress Indicator */}
                    {progress > 0 && !isCompleted && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 lg:h-2 bg-black/30">
                        <div 
                          className="h-full bg-blue-400 transition-all duration-300" 
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                    
                    {/* Completion Badge */}
                    {isCompleted && (
                      <div className="absolute top-1 right-1 lg:top-3 lg:right-3 bg-green-500 rounded-full p-0.5 lg:p-1 shadow-md">
                        <CheckCircle className="w-3 h-3 lg:w-5 lg:h-5 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Content - Flexible */}
                  <div className="flex-1 min-w-0 flex flex-col gap-2 lg:p-4">
                    {/* Title & Duration */}
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <h3 className="text-sm lg:text-base font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors flex-1 min-w-0">
                        {module.title}
                      </h3>
                      <div className="flex items-center gap-1 text-gray-500 text-xs lg:text-sm flex-shrink-0">
                        <Clock className="w-3 h-3 lg:w-4 lg:h-4" />
                        <span className="whitespace-nowrap">{module.estMinutes || 0}m</span>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-xs lg:text-sm text-gray-600 line-clamp-2 min-w-0">
                      {(module as ModuleEx).description || module.summary || 'No description available'}
                    </p>

                    {/* Badge & Status Row */}
                    <div className="flex items-center justify-between gap-2 min-w-0 mt-auto">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 lg:px-2.5 lg:py-1 rounded-full text-[10px] lg:text-xs font-medium ${badge.bg} ${badge.text} flex-shrink-0`}>
                        <badge.Icon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                        {badge.label}
                      </span>
                      
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isCompleted && (
                          <span className="text-[10px] lg:text-xs font-medium text-green-600 whitespace-nowrap">Completed</span>
                        )}
                        {!isCompleted && progress > 0 && (
                          <span className="text-[10px] lg:text-xs font-medium text-blue-600 whitespace-nowrap">{Math.round(progress)}%</span>
                        )}
                        <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredModules.length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No modules found</h3>
            <p className="text-sm text-gray-600 mb-6">
              {searchTerm 
                ? "Try adjusting your search terms" 
                : "No training modules available yet"}
            </p>
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSortBy('newest');
                }}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
              >
                <Sparkles className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Quick Stats Footer */}
        {filteredModules.length > 0 && (
          <div className="mt-6 lg:mt-8 p-4 lg:p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl lg:rounded-2xl border border-blue-100">
            <div className="flex items-center justify-center gap-2 text-sm lg:text-base text-blue-900">
              <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="font-medium">
                {filteredModules.length} {filteredModules.length === 1 ? 'module' : 'modules'} available
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

